/* eslint-disable perfectionist/sort-modules */

import type {
  BatterySortableFields,
  HybridOption,
  InverterSortableFields,
  MicroInverterSortableFields,
  ModuleSortableFields,
  OrderByOption,
  SelectOption,
  TableColumn,
} from '~/types/equipment';

export const EQUIPMENT_VIEW_ROLE = 'equipment:view';
export const EQUIPMENT_EDIT_ROLE = 'equipment:edit';
export const DEBOUNCE_DELAY = 1000;
export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE = 1;
export const EMPTY_STRING = '';

export const ORDER_BY_LABELS = {
  CREATED_AT_ASC: 'Data de inclusão (mais antiga)',
  CREATED_AT_DESC: 'Data de inclusão (mais recente)',
  POWER_ASC: 'Potência (menor)',
  POWER_DESC: 'Potência (maior)',
} as const;

export const ORDER_BY_OPTIONS: readonly SelectOption<OrderByOption>[] = [
  { label: ORDER_BY_LABELS.CREATED_AT_ASC, value: 'created_at:asc' },
  { label: ORDER_BY_LABELS.CREATED_AT_DESC, value: 'created_at:desc' },
  { label: ORDER_BY_LABELS.POWER_ASC, value: 'power:asc' },
  { label: ORDER_BY_LABELS.POWER_DESC, value: 'power:desc' },
] as const;

export const ENABLED_OPTION_LABELS = {
  ALL: 'Todos',
  DISABLED: 'Desativado',
  ENABLED: 'Ativado',
} as const;

export const ENABLED_OPTIONS: readonly SelectOption<boolean | undefined>[] = [
  { label: ENABLED_OPTION_LABELS.ALL, value: undefined },
  { label: ENABLED_OPTION_LABELS.DISABLED, value: false },
  { label: ENABLED_OPTION_LABELS.ENABLED, value: true },
] as const;

// Unified labels for all equipment types
export const EQUIPMENT_LABELS = {
  ACTIONS: 'Ações',
  BRAND: 'Marca',
  COUNT_KEY: 'Chave Contadora / Disjuntor',
  ENABLED: 'Status',
  HYBRID: 'Híbrido',
  LAST_UPDATE: 'Última Atualização',
  MODEL: 'Modelo',
  OVERLOAD: 'Overload Permitido (%)',
  PHASE: 'Fase',
  POWER: 'Potência (kW)',
  SKUS: 'SKUs',
  STATUS: 'Status',
  TENSION: 'Tensão (V)',
  TYPE: 'Tipo',
} as const;

// Common placeholders for form inputs
export const FORM_PLACEHOLDERS = {
  BRAND_FILTER: 'Digite a marca',
  BRAND_SEARCH: 'Digite para buscar marca ou criar nova',
  BRAND_SELECT: 'Selecione ou digite a marca',
  COUNT_KEY: 'Digite a chave contadora',
  MODEL: 'Digite o modelo',
  MODEL_FILTER: 'Digite o modelo',
  OVERLOAD: 'Ex: 2(representa 100%)',
  POWER: 'Digite a potência em kW',
  POWER_FILTER: 'Digite a potência em kW',
  SKU_FILTER: 'Digite o SKU',
  SKUS: 'Digite o(s) SKU(s) e pressione ENTER para adicionar',
  TENSION: 'Digite a tensão em Volts',
} as const;

export const FORM_HELP = {
  SKUS: 'Digite o(s) SKU(s) associados em Loja (se houver) e pressione ENTER para adicionar.',
} as const;

// Button text labels
export const BUTTON_LABELS = {
  CANCEL: 'Cancelar',
  CLEAR: 'Limpar',
  CREATE: 'Cadastrar',
  EDIT: 'Editar',
  SAVE: 'Salvar',
  SEARCH: 'Buscar',
} as const;

// Modal titles for equipment types
export const MODAL_TITLES = {
  BATTERY: {
    CREATE: 'Cadastrar Bateria',
    EDIT: 'Editar Bateria',
    VIEW: 'Detalhes da Bateria',
  },
  INVERTER: {
    CREATE: 'Cadastrar Inversor',
    EDIT: 'Editar Inversor',
    VIEW: 'Detalhes do Inversor',
  },
  MICROINVERTER: {
    CREATE: 'Cadastrar Micro-inversor',
    EDIT: 'Editar Micro-inversor',
    VIEW: 'Detalhes do Micro-inversor',
  },
  MODULE: {
    CREATE: 'Cadastrar Módulo',
    EDIT: 'Editar Módulo',
    VIEW: 'Detalhes do Módulo',
  },
} as const;

// Action button labels for equipment types
export const EQUIPMENT_BUTTON_LABELS = {
  CREATE_BATTERY: 'Cadastrar Bateria',
  CREATE_INVERTER: 'Cadastrar Inversor',
  CREATE_MICROINVERTER: 'Cadastrar Micro-inversor',
  CREATE_MODULE: 'Cadastrar Módulo',
} as const;

// Status text labels
export const STATUS_LABELS = {
  DISABLED: 'Desativado',
  ENABLED: 'Ativado',
  HYBRID: 'Híbrido',
  NON_HYBRID: 'Não Híbrido',
} as const;

// Validation error messages
export const VALIDATION_MESSAGES = {
  BRAND_INVALID_TYPE: 'Marca deve ser um texto válido',
  BRAND_MAX_LENGTH: 'Marca deve ter no máximo 100 caracteres',
  BRAND_REQUIRED: 'Marca é obrigatória',

  COUNT_KEY_INVALID_TYPE: 'Chave contadora deve ser um número válido',
  COUNT_KEY_MIN: 'Chave contadora deve ser maior que 0',
  COUNT_KEY_REQUIRED: 'Chave contadora é obrigatória',

  MODEL_INVALID_TYPE: 'Modelo deve ser um texto válido',
  MODEL_MAX_LENGTH: 'Modelo deve ter no máximo 100 caracteres',
  MODEL_REQUIRED: 'Modelo é obrigatório',
  OVERLOAD_INVALID_TYPE: 'Overload deve ser um número válido',

  OVERLOAD_MAX: 'Digite o valor em porcentagem (ex: 2 para 100%, 1.98 para 98%). Máximo permitido: 5',
  OVERLOAD_MIN: 'Overload deve ser maior que 0',

  OVERLOAD_REQUIRED: 'Overload é obrigatório',
  PHASE_INVALID_TYPE: 'Fase deve ser um número válido',
  PHASE_RANGE: 'Fase deve ser 1, 2 ou 3',

  PHASE_REQUIRED: 'Fase é obrigatória',
  POWER_INVALID_TYPE: 'Potência deve ser um número válido',
  POWER_MAX: 'Potência deve ser menor ou igual a 999kW',

  POWER_MIN: 'Potência deve ser maior que 0',
  POWER_REQUIRED: 'Potência é obrigatória',
  STATUS_INVALID_TYPE: 'Selecione Ativado ou Desativado',
  STATUS_REQUIRED: 'Campo Status é obrigatório',

  TENSION_INVALID_TYPE: 'Tensão deve ser um número válido',
  TENSION_MIN: 'Tensão deve ser maior que 0',
  TENSION_REQUIRED: 'Tensão é obrigatória',

  TYPE_INVALID_TYPE: 'Selecione o tipo do equipamento',
  TYPE_REQUIRED: 'Tipo é obrigatório',
} as const;

export const getSkusDuplicateMessage = (sku: string) => `Este SKU '${sku}' já foi adicionado`;

export const PHASE_OPTIONS: readonly SelectOption<number>[] = [
  { label: 'Monofásico (1)', value: 1 },
  { label: 'Bifásico (2)', value: 2 },
  { label: 'Trifásico (3)', value: 3 },
] as const;

export const HYBRID_OPTION_LABELS = {
  ALL: 'Todos',
  NO: 'Não',
  YES: 'Sim',
} as const;

export const HYBRID_OPTIONS: readonly SelectOption<HybridOption>[] = [
  { label: HYBRID_OPTION_LABELS.ALL, value: undefined },
  { label: HYBRID_OPTION_LABELS.YES, value: true },
  { label: HYBRID_OPTION_LABELS.NO, value: false },
] as const;

// ============================================================================
// TABLE COLUMN DEFINITIONS FOR EACH EQUIPMENT TYPE
// ============================================================================

export const BATTERY_TABLE_COLUMNS: readonly TableColumn<BatterySortableFields>[] = [
  { name: EQUIPMENT_LABELS.BRAND, sort: true, value: 'brand' },
  { name: EQUIPMENT_LABELS.MODEL, sort: true, value: 'model' },
  { name: EQUIPMENT_LABELS.POWER, sort: true, value: 'power' },
  { name: EQUIPMENT_LABELS.SKUS, sort: false, value: 'skus' },
  { name: EQUIPMENT_LABELS.ENABLED, sort: true, value: 'is_enabled' },
  { name: EQUIPMENT_LABELS.ACTIONS, sort: false, value: 'actions' },
] as const;

export const MODULE_TABLE_COLUMNS: readonly TableColumn<ModuleSortableFields>[] = [
  { name: EQUIPMENT_LABELS.BRAND, sort: true, value: 'brand' },
  { name: EQUIPMENT_LABELS.MODEL, sort: true, value: 'model' },
  { name: EQUIPMENT_LABELS.POWER, sort: true, value: 'power' },
  { name: EQUIPMENT_LABELS.SKUS, sort: false, value: 'skus' },
  { name: EQUIPMENT_LABELS.ENABLED, sort: true, value: 'enabled' },
  { name: EQUIPMENT_LABELS.ACTIONS, sort: false, value: 'actions' },
] as const;

export const INVERTER_TABLE_COLUMNS: readonly TableColumn<InverterSortableFields>[] = [
  { name: EQUIPMENT_LABELS.BRAND, sort: true, value: 'brand' },
  { name: EQUIPMENT_LABELS.MODEL, sort: true, value: 'model' },
  { name: EQUIPMENT_LABELS.POWER, sort: true, value: 'power' },
  { name: EQUIPMENT_LABELS.SKUS, sort: false, value: 'skus' },
  { name: EQUIPMENT_LABELS.TENSION, sort: true, value: 'tension' },
  { name: EQUIPMENT_LABELS.COUNT_KEY, sort: true, value: 'count_key' },
  { name: EQUIPMENT_LABELS.PHASE, sort: true, value: 'phase_id' },
  { name: EQUIPMENT_LABELS.HYBRID, sort: true, value: 'is_hybrid' },
  { name: 'Combo Conecte', sort: true, value: 'is_combo_conecte' },
  { name: EQUIPMENT_LABELS.OVERLOAD, sort: true, value: 'overload' },
  { name: EQUIPMENT_LABELS.ENABLED, sort: true, value: 'is_enabled' },
  { name: EQUIPMENT_LABELS.ACTIONS, sort: false, value: 'actions' },
] as const;

export const MICROINVERTER_TABLE_COLUMNS: readonly TableColumn<MicroInverterSortableFields>[] = [
  { name: EQUIPMENT_LABELS.BRAND, sort: true, value: 'brand' },
  { name: EQUIPMENT_LABELS.MODEL, sort: true, value: 'model' },
  { name: EQUIPMENT_LABELS.POWER, sort: true, value: 'power' },
  { name: EQUIPMENT_LABELS.SKUS, sort: false, value: 'skus' },
  { name: EQUIPMENT_LABELS.TENSION, sort: true, value: 'tension' },
  { name: EQUIPMENT_LABELS.COUNT_KEY, sort: true, value: 'count_key' },
  { name: EQUIPMENT_LABELS.PHASE, sort: true, value: 'phase_id' },
  { name: EQUIPMENT_LABELS.HYBRID, sort: true, value: 'is_hybrid' },
  { name: 'Combo Conecte', sort: true, value: 'is_combo_conecte' },
  { name: EQUIPMENT_LABELS.OVERLOAD, sort: true, value: 'overload' },
  { name: EQUIPMENT_LABELS.ENABLED, sort: true, value: 'is_enabled' },
  { name: EQUIPMENT_LABELS.ACTIONS, sort: false, value: 'actions' },
] as const;
